import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import type { DashboardDateFilterState } from '@/features/Dashboard/components/DashboardDateFilter';
import service from '@/services';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';

type UseFeedbackGalleryDataParams = {
  columnId: string;
  filterSet: StructuredFilter;
  sortBy: string;
  orderBy: string;
  enabled?: boolean;
  isPublic?: boolean;
  workspaceId?: string;
  globalFilter?: StructuredFilter | null;
  globalDateFilter?: DashboardDateFilterState | null;
};

type FeedbackGalleryInfiniteQueryOptionsParams = UseFeedbackGalleryDataParams & {
  workspaceId?: string;
};

const buildEffectiveFilterSet = (
  filterSet: StructuredFilter,
  globalFilter?: StructuredFilter | null,
  globalDateFilter?: DashboardDateFilterState | null,
): StructuredFilter => {
  const mergedFilterSet = {
    ...filterSet,
    ...(globalFilter || {}),
  };

  if (globalDateFilter?.dateRange) {
    mergedFilterSet[FEEDBACK_COLUMNS.DATE_START] =
      globalDateFilter.dateRange[0].toJSON();
    mergedFilterSet[FEEDBACK_COLUMNS.DATE_END] =
      globalDateFilter.dateRange[1].toJSON();
  }

  return mergedFilterSet;
};

const feedbackGalleryQueryKeys = {
  all: ['dashboard', 'feedback-gallery'] as const,
  list: (
    columnId: string,
    sortBy: string,
    orderBy: string,
    filterSet: StructuredFilter,
    workspaceAlias?: string,
    isPublic?: boolean,
  ) =>
    [
      ...feedbackGalleryQueryKeys.all,
      columnId,
      sortBy,
      orderBy,
      filterSet,
      workspaceAlias ?? null,
      isPublic ?? false,
    ] as const,
  infiniteList: (
    columnId: string,
    sortBy: string,
    orderBy: string,
    filterSet: StructuredFilter,
    workspaceAlias?: string,
    isPublic?: boolean,
  ) =>
    [
      ...feedbackGalleryQueryKeys.list(
        columnId,
        sortBy,
        orderBy,
        filterSet,
        workspaceAlias,
        isPublic,
      ),
      'infinite',
    ] as const,
};

const feedbackGalleryInfiniteQueryOptions = (
  params: FeedbackGalleryInfiniteQueryOptionsParams,
) => {
  const {
    columnId,
    filterSet,
    sortBy,
    orderBy,
    enabled = true,
    isPublic,
    workspaceId,
  } = params;
  const effectiveFilterSet = buildEffectiveFilterSet(
    filterSet,
    params.globalFilter,
    params.globalDateFilter,
  );

  return infiniteQueryOptions({
    queryKey: feedbackGalleryQueryKeys.infiniteList(
      columnId,
      sortBy,
      orderBy,
      effectiveFilterSet,
      workspaceId,
      isPublic,
    ),
    queryFn: ({ pageParam }) =>
      service.feedback.fetchFeedbackList({
        query: {
          sortby: sortBy,
          order: orderBy,
          cursor: pageParam,
          direction: 'next',
        },
        payload: effectiveFilterSet,
        isPublic,
        workspaceAlias: workspaceId,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = lastPage.count ?? 0;
      const loadedCount = allPages.reduce((count, page) => {
        return count + (page.feedbacks?.length ?? 0);
      }, 0);
      if (loadedCount >= totalCount) return undefined;

      const lastFeedback = lastPage.feedbacks?.[lastPage.feedbacks.length - 1];
      return lastFeedback?.paginate_token;
    },
    enabled,
  });
};

const useFeedbackGalleryData = (params: UseFeedbackGalleryDataParams) => {
  const { workspace_id: routeWorkspaceId } = useParams();
  const workspaceId = params.workspaceId ?? routeWorkspaceId;

  return useInfiniteQuery(
    feedbackGalleryInfiniteQueryOptions({
      ...params,
      workspaceId,
    }),
  );
};

export {
  buildEffectiveFilterSet,
  feedbackGalleryInfiniteQueryOptions,
  feedbackGalleryQueryKeys,
  useFeedbackGalleryData,
};
